import React, { useMemo } from "react";

import { useSoilSightCurrency, useTranslation } from "utils";

import { CROP_YEAR_FIELD_LIMITS, EDIT_COLUMN_WIDTHS, EditableFieldKey, editTableMinWidth } from "consts";

import { InputNumber, Table, Tooltip, Typography } from "components";

import { CropYear } from "pages/app/scenario-analysis/types/scenario";
import { growsCoverCrop } from "pages/app/scenario-analysis/utils/cropYearHelpers";
import { visibleEditableFields } from "pages/app/scenario-analysis/utils/detailedCalculationsEdit";

type EditTableProps = {
  /** First rotation cycle only — later years mirror these and cannot be edited independently. */
  cycle: CropYear[];
  /** Draft-aware value for a field, falling back to what is stored. Keyed by crop-year id, not index. */
  valueFor: (year: CropYear, field: EditableFieldKey) => number | null;
  onChange: (yearId: string, field: EditableFieldKey, value: number | null) => void;
  cropLabelFor: (year: CropYear) => string;
  dataTestId?: string;
};

/** One row per crop year in the first rotation cycle. `key` is the crop-year id, never the index. */
type EditRow = { key: string; year: CropYear };

// The shared Table types column renders against a plain object, and feature code cannot import
// antd's row types, so the record is narrowed in one place.
const asEditRow = (record: object) => record as EditRow;

const PREFIX = "detailedCalculations.";

/** Only the inputs collected elsewhere; derived and calculated columns are omitted entirely. */
export const EditTable = ({ cycle, valueFor, onChange, cropLabelFor, dataTestId = "edit-table" }: EditTableProps) => {
  const { t } = useTranslation("scenarioAnalysis");
  const { currencySymbol } = useSoilSightCurrency();

  const unitLabel = (unit: string) => unit.replace("{{currency}}", currencySymbol);

  const rows = useMemo<EditRow[]>(() => cycle.map((year) => ({ key: year.id, year })), [cycle]);
  const fields = useMemo(() => visibleEditableFields(cycle), [cycle]);

  // No `name`: keeps the draft authoritative, but then antd emits no label pairing, hence ariaLabel.
  const renderInput = (year: CropYear, rowNumber: number, label: string, field: EditableFieldKey, coverCropOnly: boolean) => {
    const accessibleName = `${label} ${rowNumber}`;
    // A year without a cover crop is never charged for one, so the input locks — but still shows what is stored.
    const disabled = coverCropOnly && !growsCoverCrop(year);
    return (
      <InputNumber
        className="edit-table-input"
        label={accessibleName}
        ariaLabel={accessibleName}
        value={valueFor(year, field)}
        disabled={disabled}
        onChange={(next) => onChange(year.id, field, next)}
        {...CROP_YEAR_FIELD_LIMITS[field]}
        dataTestId={`${dataTestId}-${field}-${rowNumber - 1}`}
      />
    );
  };

  const columns = [
    {
      title: t(`${PREFIX}column.crop`),
      key: "crop",
      fixed: "left" as const,
      width: EDIT_COLUMN_WIDTHS.crop,
      className: "edit-table-crop-cell",
      render: (_: unknown, record: object) => cropLabelFor(asEditRow(record).year),
    },
    ...fields.map(({ key, labelKey, unit, ...field }) => {
      const label = t(`${PREFIX}column.${labelKey}`);
      return {
        title: (
          <>
            <Tooltip title={label}>
              <span className="edit-table-col-label">{label}</span>
            </Tooltip>
            <span className="edit-table-col-unit">{unitLabel(unit)}</span>
          </>
        ),
        key,
        width: EDIT_COLUMN_WIDTHS.input,
        className: "edit-table-editable",
        render: (_: unknown, record: object, index: number) =>
          renderInput(asEditRow(record).year, index + 1, label, key, "coverCropOnly" in field && field.coverCropOnly),
      };
    }),
  ];

  return (
    <div className="edit-table" data-testid={dataTestId}>
      <Typography variant="paragraph-s" color="grey-600" className="edit-table-hint">
        {t(`${PREFIX}editNote`)}
      </Typography>

      <Table<EditRow>
        className="edit-table-grid"
        data={rows}
        columns={columns}
        rowKey="key"
        pagination={false}
        scroll={{ x: editTableMinWidth(fields.length) }}
      />
    </div>
  );
};

export default EditTable;
